import React, { useState, useEffect, useMemo } from 'react';
import { 
    Search, Plus, Trash2, Edit2, Video, 
    ChevronLeft, ChevronRight, Loader2, Save, X, RefreshCw,
    Eye, EyeOff
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useToast } from '../../../context/ToastContext';
import { 
    fetchShortsFromSupabase, createShort, updateShort, deleteShort, DBShort 
} from '../../../services/youtubeVideoService';
import { VideoItem } from '../../../types';
import { ConfirmModal } from '../../Common/ConfirmModal';
import { at } from '../../../i18nAdmin';

export const ShortsTab: React.FC = () => {
    const { lang } = useLanguage();
    const { showToast } = useToast();
    const [shorts, setShorts] = useState<VideoItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination & Filter States
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isFormAnimating, setIsFormAnimating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Delete Modal State
    const [deleteModalState, setDeleteModalState] = useState<{ isOpen: boolean; id: string | null; isDeleting: boolean }>({
        isOpen: false,
        id: null,
        isDeleting: false
    });

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        video_id: '',
        channel_title: '',
        channel_image: '',
        channel_url: '',
        description: '',
        is_hidden: false
    });

    const loadData = async () => {
        try {
            setIsLoading(true);
            const data = await fetchShortsFromSupabase();
            setShorts(data);
        } catch (e) {
            showToast('error', 'Failed to load shorts');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Filtering Logic
    const filteredShorts = useMemo(() => {
        return shorts.filter(s => {
            const matchesSearch = s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                 s.channel.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesSearch;
        });
    }, [shorts, searchTerm]);

    // Pagination Logic
    const totalPages = Math.ceil(filteredShorts.length / pageSize);
    const paginatedShorts = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredShorts.slice(start, start + pageSize);
    }, [filteredShorts, currentPage, pageSize]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, pageSize]);

    const handleOpenCreate = () => {
        setEditingId(null);
        setFormData({
            title: '',
            video_id: '',
            channel_title: '',
            channel_image: '',
            channel_url: '',
            description: '',
            is_hidden: false
        });
        setIsFormOpen(true);
        setTimeout(() => setIsFormAnimating(true), 10);
    };

    const handleOpenEdit = (s: VideoItem) => {
        setEditingId(s.id);
        setFormData({
            title: s.title,
            video_id: s.videoId,
            channel_title: s.channel,
            channel_image: s.channelImage || '',
            channel_url: s.channel_url || '',
            description: s.description || '',
            is_hidden: s.is_hidden || false
        });
        setIsFormOpen(true);
        setTimeout(() => setIsFormAnimating(true), 10);
    };

    const closeForm = () => {
        setIsFormAnimating(false);
        setTimeout(() => {
            setIsFormOpen(false);
            setEditingId(null);
        }, 500);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const dataToSave = {
                title: formData.title,
                video_id: formData.video_id,
                channel_title: formData.channel_title,
                channel_image: formData.channel_image || null,
                channel_url: formData.channel_url || null,
                description: formData.description,
                is_hidden: formData.is_hidden
            };

            if (editingId) {
                await updateShort(editingId, dataToSave);
                showToast('success', at(lang, 'shortUpdated'));
            } else {
                await createShort(dataToSave);
                showToast('success', at(lang, 'shortCreated'));
            }
            closeForm();
            loadData();
        } catch (e: any) {
            showToast('error', e.message || 'Operation failed');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = (e: React.MouseEvent, id: string) => {
        e.preventDefault();
        e.stopPropagation();
        setDeleteModalState({ isOpen: true, id, isDeleting: false });
    };

    const handleToggleVisibility = async (s: VideoItem) => {
        try {
            const newHiddenStatus = !s.is_hidden;
            await updateShort(s.id, { is_hidden: newHiddenStatus } as any);
            showToast('success', at(lang, 'visibilityChanged'));
            
            // Local state update for better UX
            setShorts(prev => prev.map(item => 
                item.id === s.id ? { ...item, is_hidden: newHiddenStatus } : item
            ));
        } catch (e: any) {
            showToast('error', e.message || 'Visibility update failed');
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteModalState.id) return;
        try {
            setDeleteModalState(prev => ({ ...prev, isDeleting: true }));
            await deleteShort(deleteModalState.id);
            showToast('success', at(lang, 'deletedSuccess'));
            loadData();
        } catch (e: any) {
            console.error('Delete Error:', e);
            showToast('error', e.message || 'Delete failed');
        } finally {
            setDeleteModalState({ isOpen: false, id: null, isDeleting: false });
        }
    };

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">
                        {at(lang, 'shortsManage')}
                    </h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">
                        {at(lang, 'shortsDesc')}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={loadData}
                        className="p-3 bg-white border border-slate-200 text-slate-400 md:hover:text-slate-900 rounded-2xl shadow-sm transition-all active:scale-95"
                    >
                        <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                    </button>
                    <button 
                        onClick={handleOpenCreate}
                        className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest md:hover:bg-black transition-all active:scale-95 shadow-xl shadow-slate-200"
                    >
                        <Plus size={16} strokeWidth={3} />
                        {at(lang, 'addNew')}
                    </button>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input 
                        type="text" 
                        placeholder={at(lang, 'search')} 
                        className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-10 pr-4 text-xs font-bold text-slate-900 outline-none focus:border-rose-500 transition-all shadow-sm"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="relative lg:col-start-3">
                    <select 
                        className="w-full bg-white border border-slate-200 rounded-xl py-3 px-4 text-xs font-bold text-slate-900 outline-none focus:border-rose-500 transition-all shadow-sm cursor-pointer appearance-none"
                        value={pageSize}
                        onChange={e => setPageSize(Number(e.target.value))}
                    >
                        <option value={30}>30 {at(lang, 'itemsPerPage')}</option>
                        <option value={50}>50 {at(lang, 'itemsPerPage')}</option>
                        <option value={100}>100 {at(lang, 'itemsPerPage')}</option>
                    </select>
                </div>
            </div>

            <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                <th className="px-8 py-5 min-w-[300px]">{at(lang, 'shorts')}</th>
                                <th className="px-6 py-5 min-w-[150px]">{at(lang, 'channelName')}</th>
                                <th className="px-6 py-5 min-w-[120px]">{at(lang, 'videoId')}</th>
                                <th className="px-6 py-5 min-w-[100px] text-center">{at(lang, 'isHidden')}</th>
                                <th className="px-8 py-5 text-right min-w-[120px]">{at(lang, 'ops')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={4} className="px-8 py-20 text-center">
                                        <Loader2 className="animate-spin text-rose-500 mx-auto mb-4" size={32} />
                                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Loading Shorts...</p>
                                    </td>
                                </tr>
                            ) : paginatedShorts.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-8 py-20 text-center opacity-30">
                                        <Video size={48} className="mx-auto mb-4" />
                                        <p className="text-sm font-black uppercase tracking-widest">No Shorts Found</p>
                                    </td>
                                </tr>
                            ) : (
                                paginatedShorts.map((s) => (
                                    <tr key={s.id} className="md:hover:bg-slate-50/50 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 aspect-[9/16] rounded-lg overflow-hidden bg-slate-100 shrink-0 border border-slate-200">
                                                    <img 
                                                        src={`https://img.youtube.com/vi/${s.videoId}/mqdefault.jpg`} 
                                                        className="w-full h-full object-cover"
                                                        alt="thumbnail"
                                                    />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-black text-slate-900 line-clamp-2 uppercase tracking-tight">
                                                        {s.title}
                                                    </p>
                                                    <p className="text-[10px] font-medium text-slate-400 line-clamp-2 mt-0.5">
                                                        {s.description || 'No description'}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6">
                                            <span className="text-[11px] font-bold text-slate-600 uppercase">
                                                {s.channel}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6 font-mono text-[11px] text-indigo-500 font-bold">
                                            {s.videoId}
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <button 
                                                onClick={() => handleToggleVisibility(s)}
                                                className={`p-2.5 rounded-xl transition-all shadow-sm active:scale-90 border ${
                                                    s.is_hidden 
                                                        ? 'bg-rose-50 border-rose-100 text-rose-500 md:hover:bg-rose-100' 
                                                        : 'bg-white border-slate-200 text-slate-400 md:hover:text-slate-900 md:hover:border-slate-900'
                                                }`}
                                                title={s.is_hidden ? at(lang, 'unhide') : at(lang, 'hide')}
                                            >
                                                {s.is_hidden ? <EyeOff size={16} strokeWidth={2.5}/> : <Eye size={16} strokeWidth={2.5}/>}
                                            </button>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    onClick={() => handleOpenEdit(s)}
                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-indigo-600 md:hover:border-indigo-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                >
                                                    <Edit2 size={16}/>
                                                </button>
                                                <button 
                                                    onClick={(e) => handleDeleteClick(e, s.id)}
                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-rose-600 md:hover:border-rose-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className="p-8 border-t border-slate-100 flex items-center justify-between bg-slate-50/20">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Page {currentPage} of {totalPages || 1} ({filteredShorts.length} Items Total)
                    </p>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={totalPages === 0 || currentPage === totalPages}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Edit/Create Modal */}
            {isFormOpen && (
                <div className={`fixed inset-0 z-[500] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !m-0 ${isFormAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeForm}></div>
                    <div className={`relative bg-white w-full max-w-xl rounded-t-[40px] md:rounded-[40px] shadow-2xl overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isFormAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                                {editingId ? at(lang, 'editShort') : at(lang, 'newShort')}
                            </h3>
                            <button onClick={closeForm} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors">
                                <X size={24} />
                            </button>
                        </header>

                        <form onSubmit={handleSave} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Title</label>
                                <input 
                                    required
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                    value={formData.title}
                                    onChange={e => setFormData({...formData, title: e.target.value})}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Video ID</label>
                                    <input 
                                        required
                                        placeholder="e.g. dQw4w9WgXcQ"
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.video_id}
                                        onChange={e => setFormData({...formData, video_id: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Channel Name</label>
                                    <input 
                                        required
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.channel_title}
                                        onChange={e => setFormData({...formData, channel_title: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Channel Image URL</label>
                                    <input 
                                        placeholder="https://..."
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.channel_image}
                                        onChange={e => setFormData({...formData, channel_image: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Channel URL</label>
                                    <input 
                                        placeholder="https://youtube.com/@..."
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.channel_url}
                                        onChange={e => setFormData({...formData, channel_url: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Description</label>
                                <textarea 
                                    rows={3}
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10 resize-none"
                                    value={formData.description}
                                    onChange={e => setFormData({...formData, description: e.target.value})}
                                />
                            </div>

                            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                <input 
                                    type="checkbox"
                                    id="is_hidden"
                                    className="w-5 h-5 rounded border-slate-200 text-rose-500 focus:ring-rose-500/20 transition-all cursor-pointer"
                                    checked={formData.is_hidden}
                                    onChange={e => setFormData({...formData, is_hidden: e.target.checked})}
                                />
                                <label htmlFor="is_hidden" className="text-xs font-black text-slate-600 uppercase tracking-widest cursor-pointer select-none">
                                    {at(lang, 'isHidden')}
                                </label>
                            </div>

                            <button 
                                type="submit"
                                disabled={isSaving}
                                className="w-full py-4 bg-slate-900 text-white rounded-[20px] font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-slate-200 md:hover:bg-black transition-all active:scale-95 disabled:opacity-50 mt-4 flex items-center justify-center gap-2"
                            >
                                {isSaving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                {at(lang, 'saveShort')}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal 
                isOpen={deleteModalState.isOpen}
                onClose={() => setDeleteModalState({ isOpen: false, id: null, isDeleting: false })}
                onConfirm={handleConfirmDelete}
                isLoading={deleteModalState.isDeleting}
                title={at(lang, 'deleteConfirm')}
                description={at(lang, 'deleteConfirmDesc')}
                confirmLabel={at(lang, 'delete')}
                cancelLabel={at(lang, 'cancel')}
            />
        </div>
    );
};
